/**
 * KYCProposalCards (TASK-716) — extraction proposals under an assistant message
 * in the dock's KYC scope. The apply plumbing is the KYC scope adapter, ported
 * VERBATIM from the deleted KYCChatPanel: three routes per proposal —
 * `values.*` → KG only (DNA), `position.*` → portfolio-service SAC
 * classification (TASK-479), everything else → live form (seq signal via
 * kycBridge) + KG approve (TASK-474 human gate).
 */

import React from 'react';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import { mapKGFieldToFormPath } from '../../utils/kgFieldMapping';
import { approveFields } from '../../services/knowledgeGraphService';
import { useKYCBridge, type ExtractionProposalItem } from '../../stores/kycBridge';
import ExtractionProposal from '../kyc/workspace/ExtractionProposal';

/**
 * Persist proposals to the Knowledge Graph so they survive page reload.
 * For array staging fields (wealth.source_*, income.source_*, etc.),
 * adds an index suffix so multiple entries don't supersede each other.
 */
const STAGING_PREFIXES = [
  'wealth.source_', 'income.source_', 'adverse_media.board_',
  'business.', 'background.institution_', 'background.certification_',
];

function persistToKG(clientId: string, proposals: ExtractionProposalItem[]) {
  const counters: Record<string, number> = {};
  const indexed = proposals.map((p) => {
    const isStaging = STAGING_PREFIXES.some((pfx) => p.field_path.startsWith(pfx));
    let fieldPath = p.field_path;
    if (isStaging) {
      const idx = counters[fieldPath] ?? 0;
      counters[fieldPath] = idx + 1;
      fieldPath = `${fieldPath}[${idx}]`;
    }
    return {
      field_path: fieldPath,
      value: p.value,
      confidence: p.confidence,
      source: p.source || 'user_provided',
    };
  });
  // Approve = upsert + verify: an explicit RM Apply click is the human gate (TASK-474).
  approveFields(clientId, indexed).catch((err) => {
    logger.error('[KYCProposalCards] Failed to persist to KG:', err);
  });
}

export function applyKYCProposal(clientId: string, fieldPath: string, value: unknown): void {
  // DNA items (values.*) have no KYC form field — KG only.
  if (fieldPath.startsWith('values.')) {
    persistToKG(clientId, [{ field_path: fieldPath, value, confidence: 1.0 }]);
    toast.success('Added to client DNA');
    return;
  }
  // TASK-479: position-classification proposals write the SAC to the position.
  if (fieldPath.startsWith('position.')) {
    const positionId = fieldPath.split('.')[1];
    portfolioApiService
      .setPositionClassification(positionId, String(value))
      .then(() => toast.success(`Classified position as ${String(value)}`))
      .catch((err) => {
        logger.error('[KYCProposalCards] Failed to classify position:', err);
        toast.error('Failed to classify position');
      });
    return;
  }
  const formPath = mapKGFieldToFormPath(fieldPath);
  useKYCBridge.getState().applyProposalsToForm([{ fieldPath: formPath, value }]);
  persistToKG(clientId, [{ field_path: fieldPath, value, confidence: 1.0 }]);
  toast.success(`Applied value for ${formPath.split('.').pop()}`);
}

export function applyAllKYCProposals(clientId: string, proposals: ExtractionProposalItem[]): void {
  const positionProposals = proposals.filter((p) => p.field_path.startsWith('position.'));
  positionProposals.forEach((p) => {
    portfolioApiService
      .setPositionClassification(p.field_path.split('.')[1], String(p.value))
      .catch((err) => logger.error('[KYCProposalCards] Failed to classify position:', err));
  });

  const kgProposals = proposals.filter((p) => !p.field_path.startsWith('position.'));
  const formProposals = kgProposals.filter((p) => !p.field_path.startsWith('values.'));
  if (formProposals.length > 0) {
    useKYCBridge.getState().applyProposalsToForm(
      formProposals.map((p) => ({ fieldPath: mapKGFieldToFormPath(p.field_path), value: p.value }))
    );
  }
  if (kgProposals.length > 0) {
    persistToKG(clientId, kgProposals);
  }
  toast.success(`Applied ${proposals.length} item${proposals.length === 1 ? '' : 's'}`);
}

export const KYCProposalCards: React.FC<{
  clientId: string;
  proposals: ExtractionProposalItem[];
}> = ({ clientId, proposals }) => {
  if (proposals.length === 0) return null;
  return (
    <div className="ml-9">
      <ExtractionProposal
        proposals={proposals}
        onApply={(fieldPath, value) => applyKYCProposal(clientId, fieldPath, value)}
        onApplyAll={() => applyAllKYCProposals(clientId, proposals)}
        onSkip={() => {}}
      />
    </div>
  );
};
